// src/pages/InterviewAssistant/ThreePlusMinusInterview.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PlusCircle, MinusCircle, BarChart2, Award } from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip
} from 'recharts';

interface Item {
  text: string;
  score: number;
}

const ThreePlusMinusInterview: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'input' | 'rate' | 'result'>('input');
  const [strengths, setStrengths] = useState<Item[]>([
    { text: '', score: 3 },
    { text: '', score: 3 },
    { text: '', score: 3 },
  ]);
  const [weaknesses, setWeaknesses] = useState<Item[]>([
    { text: '', score: 3 },
    { text: '', score: 3 },
    { text: '', score: 3 },
  ]);

  const handleStrengthChange = (index: number, value: string) => {
    const updated = [...strengths];
    updated[index].text = value;
    setStrengths(updated);
  };

  const handleWeaknessChange = (index: number, value: string) => {
    const updated = [...weaknesses];
    updated[index].text = value;
    setWeaknesses(updated);
  };

  const handleScoreChange = (type: 'strength' | 'weakness', index: number, score: number) => {
    if (type === 'strength') {
      const updated = [...strengths];
      updated[index].score = score;
      setStrengths(updated);
    } else {
      const updated = [...weaknesses];
      updated[index].score = score;
      setWeaknesses(updated);
    }
  };

  const allFilled = useMemo(
    () =>
      strengths.every(s => s.text.trim().length > 0) &&
      weaknesses.every(w => w.text.trim().length > 0),
    [strengths, weaknesses]
  );

  const handleGoToRate = () => {
    if (!allFilled) {
      alert('Пожалуйста, заполните все поля');
      return;
    }
    setStep('rate');
  };

  const handleFinish = () => setStep('result');

  const radarStrengthData = strengths.map((s, i) => ({
    name: s.text || `Сильная ${i + 1}`,
    score: s.score,
  }));
  const radarWeaknessData = weaknesses.map((w, i) => ({
    name: w.text || `Слабая ${i + 1}`,
    score: w.score,
  }));

  const avgStrength = Math.round(strengths.reduce((a, b) => a + b.score, 0) / 3);
  const avgWeakness = Math.round(weaknesses.reduce((a, b) => a + b.score, 0) / 3);
  const selfAwareness = avgStrength > avgWeakness ? 'Высокая самооценка' : avgStrength < avgWeakness ? 'Критичное самовосприятие' : 'Сбалансированная оценка';

  if (step === 'result') {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />
        <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
          <ArrowLeft size={18} /> К помощнику интервью
        </button>
        <div style={styles.header}>
          <h1 style={styles.title}>Результаты метода 3+ и 3‑</h1>
        </div>
        <div style={styles.resultsGrid}>
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><Award size={18} /> Сильные стороны</h3>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={radarStrengthData}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="name" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tick={false} />
                <Radar isAnimationActive={false} name="Сильные" dataKey="score" stroke="#2ecc71" fill="#2ecc71" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><Award size={18} /> Слабые стороны / зоны роста</h3>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={radarWeaknessData}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="name" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tick={false} />
                <Radar isAnimationActive={false} name="Слабые" dataKey="score" stroke="#e74c3c" fill="#e74c3c" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><BarChart2 size={18} /> Сводка</h3>
            <table style={styles.table}>
              <thead>
                <tr><th style={styles.th}>Тип</th><th style={styles.th}>Пункт</th><th style={styles.th}>Оценка</th></tr>
              </thead>
              <tbody>
                {strengths.map((s, i) => (
                  <tr key={`s-${i}`}>
                    <td style={styles.td}><PlusCircle size={14} color="#2ecc71" /></td>
                    <td style={styles.td}>{s.text}</td>
                    <td style={styles.td}>{s.score}/5</td>
                  </tr>
                ))}
                {weaknesses.map((w, i) => (
                  <tr key={`w-${i}`}>
                    <td style={styles.td}><MinusCircle size={14} color="#e74c3c" /></td>
                    <td style={styles.td}>{w.text}</td>
                    <td style={styles.td}>{w.score}/5</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: 16, fontWeight: 600 }}>
              Самооценка: {selfAwareness}
            </p>
            <p style={{ color: '#666', fontSize: 13, marginTop: 8 }}>
              {selfAwareness === 'Высокая самооценка'
                ? 'Кандидат уверен в своих силах, что хорошо для лидерских позиций. Важно проверить, подкреплены ли сильные стороны реальными примерами.'
                : selfAwareness === 'Критичное самовосприятие'
                ? 'Кандидат склонен к самокритике. Это может говорить о высоком уровне рефлексии, но также и о неуверенности. Задайте уточняющие вопросы о достижениях.'
                : 'Сбалансированная самооценка — признак зрелости и адекватного восприятия себя. Хороший знак для большинства позиций.'}
            </p>
          </div>
        </div>
        <button onClick={() => { setStep('input'); setStrengths([{ text: '', score: 3 }, { text: '', score: 3 }, { text: '', score: 3 }]); setWeaknesses([{ text: '', score: 3 }, { text: '', score: 3 }, { text: '', score: 3 }]); }} style={styles.resetButton}>
          Пройти заново
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />
      <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
        <ArrowLeft size={18} /> К помощнику интервью
      </button>
      <div style={styles.header}>
        <h1 style={styles.title}>Метод 3+ и 3‑</h1>
        <p style={styles.subtitle}>
          {step === 'input'
            ? 'Назовите три ваших сильных стороны и три качества, которые хотели бы улучшить (слабые стороны).'
            : 'Оцените каждое качество по шкале от 1 до 5, насколько оно у вас выражено.'}
        </p>
      </div>
      <div style={styles.card}>
        {step === 'input' && (
          <>
            <h2 style={styles.sectionTitle}><PlusCircle size={20} color="#2ecc71" /> Сильные стороны</h2>
            {strengths.map((s, idx) => (
              <input
                key={`s-${idx}`}
                type="text"
                placeholder={`Сильная сторона ${idx + 1}`}
                value={s.text}
                onChange={e => handleStrengthChange(idx, e.target.value)}
                style={styles.input}
              />
            ))}
            <h2 style={{ ...styles.sectionTitle, marginTop: 28 }}><MinusCircle size={20} color="#e74c3c" /> Зоны роста</h2>
            {weaknesses.map((w, idx) => (
              <input
                key={`w-${idx}`}
                type="text"
                placeholder={`Зона роста ${idx + 1}`}
                value={w.text}
                onChange={e => handleWeaknessChange(idx, e.target.value)}
                style={styles.input}
              />
            ))}
            <button onClick={handleGoToRate} disabled={!allFilled} style={{ ...styles.navButton, background: allFilled ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : '#ccc', color: 'white', border: 'none', marginTop: 28, width: '100%' }}>
              Далее: оценить выраженность
            </button>
          </>
        )}
        {step === 'rate' && (
          <>
            <h2 style={styles.sectionTitle}><PlusCircle size={20} color="#2ecc71" /> Сильные стороны</h2>
            {strengths.map((s, idx) => (
              <div key={`rs-${idx}`} style={styles.rateRow}>
                <span style={styles.rateLabel}>{s.text}</span>
                <div style={styles.rateButtons}>
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      style={{
                        ...styles.rateButton,
                        background: s.score === val ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : 'rgba(255,255,255,0.5)',
                        color: s.score === val ? 'white' : '#1a1a2e',
                      }}
                      onClick={() => handleScoreChange('strength', idx, val)}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <h2 style={{ ...styles.sectionTitle, marginTop: 28 }}><MinusCircle size={20} color="#e74c3c" /> Зоны роста</h2>
            {weaknesses.map((w, idx) => (
              <div key={`rw-${idx}`} style={styles.rateRow}>
                <span style={styles.rateLabel}>{w.text}</span>
                <div style={styles.rateButtons}>
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      style={{
                        ...styles.rateButton,
                        background: w.score === val ? 'linear-gradient(135deg, #e74c3c, #c0392b)' : 'rgba(255,255,255,0.5)',
                        color: w.score === val ? 'white' : '#1a1a2e',
                      }}
                      onClick={() => handleScoreChange('weakness', idx, val)}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
              <button onClick={() => setStep('input')} style={styles.navButton}>Назад</button>
              <button onClick={handleFinish} style={{ ...styles.navButton, background: 'linear-gradient(135deg, #2ecc71, #27ae60)', color: 'white', border: 'none', flex: 1 }}>
                Завершить и получить результат
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '80px 20px 20px 20px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative', 
    overflowX: 'clip',                     // вместо 'hidden', чтобы не создавать контекст скролла
    willChange: 'transform',              // ускоряет композитинг
    transform: 'translateZ(0)',           // создаёт отдельный слой

  },
  bgBlur1: {
    position: 'absolute', top: '-10%', left: '-5%', width: 600, height: 600,
    background: 'radial-gradient(circle, rgba(106,17,203,0.12) 0%, rgba(106,17,203,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute', bottom: '-10%', right: '-5%', width: 700, height: 700,
    background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  backButton: {
    position: 'absolute', top: 24, left: 24,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 40,
    padding: '10px 20px', fontSize: 15, fontWeight: 500, color: '#2575fc',
    cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  header: { textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 },
  title: {
    fontSize: 36, fontWeight: 700,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: '0 0 8px',
  },
  subtitle: { fontSize: 16, color: '#666', margin: 0 },
  card: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: '40px 32px', border: '1px solid rgba(0,0,0,0.06)',
    maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1,
  },
  sectionTitle: { fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, color: '#1a1a2e' },
  input: {
    width: '100%', padding: '14px 20px', borderRadius: 16, border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.7)', fontSize: 15, marginBottom: 12, outline: 'none',
  },
  rateRow: { marginBottom: 20 },
  rateLabel: { display: 'block', fontWeight: 500, marginBottom: 8, color: '#1a1a2e' },
  rateButtons: { display: 'flex', gap: 8 },
  rateButton: {
    width: 44, height: 44, borderRadius: 22, border: '1px solid rgba(0,0,0,0.1)',
    cursor: 'pointer', fontSize: 16, fontWeight: 600, transition: 'all 0.2s',
  },
  navButton: {
    padding: '12px 24px', borderRadius: 30, border: '1px solid #2575fc', background: 'transparent',
    color: '#2575fc', cursor: 'pointer', fontWeight: 600, fontSize: 16, transition: 'all 0.2s',
  },
  resultsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: 28, maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  resultCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 32, border: '1px solid rgba(0,0,0,0.06)',
  },
  chartTitle: { fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, color: '#1a1a2e' },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 16 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.1)', color: '#666', fontWeight: 600 },
  td: { padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: 14 },
  resetButton: {
    marginTop: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto',
    padding: '12px 28px', borderRadius: 30, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
  },
};

export default ThreePlusMinusInterview;